<script setup lang="ts">
import type { components } from "assets/api/openapi"

interface Props {
  groupName: string
  assessments: components["schemas"]["TrainingAssessment"][]
  configurationType: "HOUR_COMPARISON" | "HOUR_DEFICIT"
  isLast?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isLast: false,
})

const calculateDifference = (actual: number = 0, required: number = 0) => {
  return actual - required
}

const getDifferenceColor = (difference: number) => {
  if (difference >= 0) return "text-green-600"
  if (difference < 0) return "text-red-600"
  return "text-gray-600"
}

const columns = computed(() => {
  const baseColumns = [
    {
      key: "foreignTrainingName",
      label: "Fach",
      class: "text-left w-full",
    },
    {
      key: "actualHoursCount",
      label: "SOLL-Stunden DE",
      class: "text-center min-w-32 max-w-32",
    },
    {
      key: "actualHoursApplicant",
      label: "IST-Stunden Antragsteller",
      class: "text-center min-w-32 max-w-32",
    },
    {
      key: "difference",
      label: "Differenz",
      class: "text-center min-w-24 max-w-24",
    },
  ]

  if (props.configurationType === "HOUR_COMPARISON") {
    baseColumns.push({
      key: "refreshTrainingHoursCount",
      label: "Nachschulung",
      class: "text-center min-w-32 max-w-32",
    })
  } else {
    baseColumns.push({
      key: "deficit",
      label: "Defizit festgestellt",
      class: "text-center min-w-32 max-w-32",
    })
  }

  return baseColumns
})

const totalRequired = computed(() =>
  props.assessments.reduce(
    (sum, a) => sum + (a.refreshTrainingHoursCount || 0),
    0
  )
)

const totalActual = computed(() =>
  props.assessments.reduce((sum, a) => sum + (a.actualHoursCount || 0), 0)
)

const totalDifference = computed(() =>
  calculateDifference(totalActual.value, totalActualHoursApplicant.value)
)

const totalActualHoursApplicant = computed(() =>
  props.assessments.reduce((sum, a) => sum + (a.actualHoursApplicant || 0), 0)
)

const rowsWithTotals = computed(() => {
  return [
    ...props.assessments,
    {
      id: -1,
      foreignTrainingName: "Summe",
      refreshTrainingHoursCount: totalRequired.value,
      actualHoursCount: totalActual.value,
      difference: totalDifference.value,
      actualHoursApplicant: totalActualHoursApplicant.value,
      isTotalRow: true,
    },
  ]
})

const onAssessmentChange = () => {
  if (props.configurationType === "HOUR_DEFICIT") {
    return
  }
  props.assessments.forEach((a) => {
    a.deficit = Boolean(a?.actualHoursApplicant ?? 0 > 0)
  })
}

const isAggregated = computed<boolean>(() => props.assessments.length === 1)

watch(() => props.assessments, onAssessmentChange, { deep: true })
</script>

<template>
  <IffContentSection :headline="groupName" :is-last-child="isLast">
    <div class="border rounded-lg overflow-hidden assessment-table">
      <UTable :rows="rowsWithTotals" :columns="columns" class="bg-white">
        <template #foreignTrainingName-data="{ row }">
          <div :class="{ 'font-bold': row.isTotalRow }" class="text-wrap">
            <div class="text-sm" :class="{ 'font-medium': !row.isTotalRow }">
              {{
                isAggregated && !row.isTotalRow
                  ? `Nachzuschulende ${groupName}stunden als Gesamtstundenzahl`
                  : row.foreignTrainingName
              }}
            </div>
            <div
              v-if="row.maxHoursCountRefreshTraining && !row.isTotalRow"
              class="text-xs text-gray-500 mt-0.5"
            >
              max. Nachschulung: {{ row.maxHoursCountRefreshTraining }} Stunden
            </div>
          </div>
        </template>

        <template #actualHoursCount-data="{ row }">
          <div class="text-center">
            <span
              class="text-sm"
              :class="{
                'font-bold': row.isTotalRow,
              }"
            >
              {{ row.actualHoursCount || 0 }}
            </span>
          </div>
        </template>

        <template #actualHoursApplicant-data="{ row }">
          <div class="flex justify-center">
            <UInput
              v-if="!row.isTotalRow"
              v-model.number="row.actualHoursApplicant"
              type="number"
              size="sm"
              class="w-24"
              min="0"
            />
            <span v-else class="text-sm font-bold">
              {{ row.actualHoursApplicant }}
            </span>
          </div>
        </template>

        <template #difference-data="{ row }">
          <div class="text-center">
            <span
              :class="[
                getDifferenceColor(
                  calculateDifference(
                    row.actualHoursApplicant,
                    row.actualHoursCount
                  )
                ),
                { 'font-bold': true },
              ]"
              class="text-sm"
            >
              {{
                calculateDifference(
                  row.actualHoursApplicant,
                  row.actualHoursCount
                )
              }}
            </span>
          </div>
        </template>

        <template #refreshTrainingHoursCount-data="{ row }">
          <div class="flex justify-center">
            <UInput
              v-if="!row.isTotalRow"
              v-model.number="row.refreshTrainingHoursCount"
              type="number"
              size="sm"
              class="w-24"
              min="0"
            />
            <span v-else class="text-sm font-bold">
              {{ row.refreshTrainingHoursCount }}
            </span>
          </div>
        </template>

        <template #deficit-data="{ row }">
          <div class="flex justify-center items-center">
            <UCheckbox v-if="!row.isTotalRow" v-model="row.deficit" />
            <span v-else class="text-sm font-bold">-</span>
          </div>
        </template>
      </UTable>
    </div>
  </IffContentSection>
</template>

<style scoped>
.assessment-table :deep(tbody tr:last-child) {
  @apply bg-primary-100;
}
</style>
